import { useState } from "react";
import { ShareNetwork as Share2, DownloadSimple as Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { wrapLines } from "./RecapCard";
import { mask } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#FAF7F0", ink: "#2A1E5C", ink2: "#5D5A6B", muted: "#8B7E6A", gold: "#F5CB5C", green: "#3E8E4E", line: "#E8E2D5" };
const money = (n) => mask(`$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const monthYr = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

function drawSeason(d, hideAmounts) {
  const W = 1080, H = 1350;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");

  x.fillStyle = C.bg; x.fillRect(0, 0, W, H);
  x.fillStyle = C.ink; x.fillRect(0, 0, W, 16);
  x.textAlign = "center";

  x.fillStyle = C.muted; x.font = "bold 34px Arial";
  x.fillText("🏁 P A Y D A Y   R E L A Y ", W / 2, 130);
  x.font = "30px Arial";
  x.fillText("RELAY RACE HIGHLIGHTS", W / 2, 178);

  x.fillStyle = C.ink; x.font = "bold 52px Georgia";
  const range = d.season_start
    ? `${monthYr(d.season_start)} – ${d.season_end ? monthYr(d.season_end) : "today"}`
    : "My race so far";
  x.fillText(range, W / 2, 280);

  if (hideAmounts) {
    x.fillStyle = C.green; x.font = "bold 46px Arial";
    x.fillText("RELAYS CLEARED", W / 2, 470);
    x.fillStyle = C.ink; x.font = "bold 170px Georgia";
    x.fillText(`${d.cleared}`, W / 2, 650);
    x.fillStyle = C.ink2; x.font = "38px Arial";
    x.fillText("weekly targets turned into real income", W / 2, 720);
  } else {
    x.fillStyle = C.green; x.font = "bold 46px Arial";
    x.fillText("EARNED THIS RACE", W / 2, 470);
    x.fillStyle = C.ink; x.font = "bold 150px Georgia";
    x.fillText(money(d.total_earned), W / 2, 645);
    x.fillStyle = C.ink2; x.font = "38px Arial";
    x.fillText(`across ${d.weeks_total} relay${d.weeks_total === 1 ? "" : "s"}`, W / 2, 720);
  }

  let y = 830;
  x.fillStyle = C.ink; x.font = "bold 44px Arial";
  x.fillText(`🔥 ${d.cleared} of ${d.weeks_total} relays cleared`, W / 2, y);
  y += 70;
  if (!hideAmounts && d.surplus_total > 0) {
    x.fillStyle = C.green; x.font = "40px Arial";
    x.fillText(`💰 ${money(d.surplus_total)} bonus banked`, W / 2, y);
    y += 64;
  }
  if (d.best_week && d.best_week.earned > 0) {
    x.fillStyle = C.ink2; x.font = "40px Arial";
    const bw = d.best_week;
    x.fillText(
      hideAmounts
        ? `🏆 Best week: Relay #${bw.relay_number || "?"}`
        : `🏆 Best week: ${money(bw.earned)}${bw.relay_number ? ` (Relay #${bw.relay_number})` : ""}`,
      W / 2, y
    );
    y += 64;
  }

  // Time off, the rest they built into the race
  const t = d.time_off || {};
  const rest = [];
  if (t.pto_days) rest.push(`🌴 ${t.pto_days} PTO day${t.pto_days === 1 ? "" : "s"}`);
  if (t.pto_weeks) rest.push(`${t.pto_weeks} PTO week${t.pto_weeks === 1 ? "" : "s"}`);
  if (t.sick_days) rest.push(`🤒 ${t.sick_days} sick day${t.sick_days === 1 ? "" : "s"}`);
  if (t.days_off) rest.push(`😌 ${t.days_off} day${t.days_off === 1 ? "" : "s"} off`);
  if (!hideAmounts && t.covered_total > 0) rest.push(`${money(t.covered_total)} self-paid`);
  if (rest.length) {
    x.fillStyle = "#2E86AB"; x.font = "34px Arial";
    const restLines = wrapLines(x, rest.join("  ·  "), 940, 2);
    for (const ln of restLines) { x.fillText(ln, W / 2, y); y += 46; }
  }

  if (d.recap_quote && d.recap_quote.text) {
    x.fillStyle = C.ink; x.font = "italic 36px Georgia";
    const q0 = Math.max(y + 50, 1085);
    const maxL = Math.max(1, Math.min(3, Math.floor((1185 - q0) / 48) + 1));
    const lines = wrapLines(x, `“${d.recap_quote.text}”`, 800, maxL);
    let qy = q0;
    for (const ln of lines) { x.fillText(ln, W / 2, qy); qy += 48; }
  }

  x.strokeStyle = C.line; x.lineWidth = 2;
  x.beginPath(); x.moveTo(200, 1205); x.lineTo(W - 200, 1205); x.stroke();
  x.fillStyle = C.muted; x.font = "34px Arial";
  x.fillText("Run your own race → paydayrelay.com", W / 2, 1262);

  return cv;
}

export const SeasonReel = () => {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [hideAmounts, setHideAmounts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/caddy/season`, { credentials: "include" });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Couldn't load your race"); return; }
      if (!d.weeks_total) {
        toast.message("No relays run yet", { description: "Log a few wins first. Then come back for your reel." });
        return;
      }
      setData(d);
      setPreview(drawSeason(d, hideAmounts).toDataURL("image/png"));
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleHide = (v) => {
    setHideAmounts(v);
    if (data) setPreview(drawSeason(data, v).toDataURL("image/png"));
  };

  const share = async () => {
    if (busy || !data) return;
    setBusy(true);
    try {
      const cv = drawSeason(data, hideAmounts);
      const blob = await new Promise((r) => cv.toBlob(r, "image/png"));
      const file = new File([blob], "payday-relay-race.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Payday Relay relay race" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "payday-relay-race.png";
        a.click();
        toast.success("Race reel saved, that's a whole race of showing up 🏁");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Couldn't share, try the download instead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl p-5 lg:p-6 pr-card pr-light" data-testid="caddy-season-card">
      <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.muted }}>Relay race highlights reel</p>
      <p className="text-[13px] leading-relaxed mb-3" style={{ color: C.ink2 }}>
        Your whole 52-week relay race on one card, total earned, relays cleared, best week, and your featured reflection.
      </p>
      {preview && (
        <img src={preview} alt="Relay race highlights card preview" className="w-full rounded-xl border mb-3" style={{ borderColor: C.line }} data-testid="season-preview" />
      )}
      {data && (
        <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer" style={{ color: C.ink2 }}>
          <input
            type="checkbox"
            checked={hideAmounts}
            onChange={(e) => toggleHide(e.target.checked)}
            className="w-4 h-4"
            data-testid="season-hide-amounts"
          />
          Hide dollar amounts (show relay counts only)
        </label>
      )}
      <div className="flex gap-2">
        {!preview ? (
          <button
            type="button"
            onClick={create}
            disabled={loading}
            className="flex-1 py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40 transition-transform hover:scale-[1.02]"
            style={{ background: C.ink, color: "#fff" }}
            data-testid="season-create-btn"
          >
            {loading ? "Adding up your race…" : "🎞️ Create my race reel"}
          </button>
        ) : (
          <button
            type="button"
            onClick={share}
            disabled={busy}
            className="flex-1 py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40 transition-transform hover:scale-[1.02]"
            style={{ background: C.ink, color: "#fff" }}
            data-testid="season-share-btn"
          >
            {navigator.canShare ? <Share2 className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            {busy ? "Working…" : navigator.canShare ? "Share it" : "Download it"}
          </button>
        )}
      </div>
    </div>
  );
};
